import React, { Component } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import { tableOptions } from '../../lib/table-utils';
import { getUrlSearchResults, urlParamFilter } from '../../lib/utils';
import { SORT_DESC, SORT_DIR_URL_NAME, SORT_URL_NAME } from '../../lib/routes/routeHelper';

/**
 * Passes down 2 child properties:
 *  tableOptions: { onFilterChange } - listen for filter changes to stop the URL filter override
 *  setUrlColumnFilters(columnData, filters) - called to set/override filters with those specified from the URL; disabled with MUI.onFilterChange
 * @param WrappedComponent
 * @param urlColumnNameMap:
 */
const withUrlProperties = (WrappedComponent, urlColumnNameMap) =>
// eslint-disable-next-line implicit-arrow-linebreak
  class extends Component {
    constructor(props) {
      super(props);
      // for each key, create a fn to parse the value from the url
      //   the fn will take: urlParseResults: {}, columnData: {}, filterData: [[],]
      const urlKeys = keys(urlColumnNameMap);
      const convertUrlKeysToProperties = urlKeys.map(
        (urlKey) => (results, columns, filters) => {
          urlParamFilter(urlKey, urlColumnNameMap, results, columns, filters);
        },
      );

      this.state = {
        shouldApplyURLFilters: true,
        convertUrlKeysToProperties,
      };

      this.setUrlColumnFilters = this.setUrlColumnFilters.bind(this);
    }

    get tableOptions() {
      const { shouldApplyURLFilters } = this.state;
      const updatedOptions = { ...tableOptions };

      // hook to disable state
      const previousOnFilterChange = updatedOptions.onFilterChange;
      updatedOptions.onFilterChange = (changedColumn, filterList) => {
        // apply the URL filters, until the user adds or removes a filter; don't want to permanently override
        // FIXME arguably, this should mutate the URL to drop the filters based on changedColumn; URL and filter-state won't be in sync
        if (shouldApplyURLFilters) this.setState(() => ({ shouldApplyURLFilters: false }));

        if (previousOnFilterChange) previousOnFilterChange(changedColumn, filterList);
      };

      return updatedOptions;
    }

    setUrlColumnFilters(columnData) {
      const { convertUrlKeysToProperties, shouldApplyURLFilters } = this.state;
      if (!shouldApplyURLFilters) return columnData;

      const urlResults = getUrlSearchResults();
      if (isEmpty(urlResults)) return columnData;

      const overriddenFilters = columnData.map(() => []);
      convertUrlKeysToProperties.forEach((filterFn) => {
        filterFn(urlResults, columnData, overriddenFilters);
      });

      const sortName = get(urlResults, SORT_URL_NAME);
      const sortDirection = get(urlResults, SORT_DIR_URL_NAME, SORT_DESC);

      for (let i = 0; i < columnData.length; i++) {
        const thisCol = columnData[i];
        set(thisCol, 'options.filterList', overriddenFilters[i]);

        if (shouldApplyURLFilters && sortName === thisCol.name) {
          set(thisCol, 'options.sortOrder', sortDirection);
        }
      }

      return columnData;
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          tableOptions={this.tableOptions}
          setUrlColumnFilters={this.setUrlColumnFilters}
        />
      );
    }
  };

export default withUrlProperties;
